import { RuleSetRoot } from '@/views/modules/segment-v2/types/ruleset-payload';
import { TreeNode } from '@/views/modules/segment-v2/types/segment';
import brandService from '@/services/brand';
import { acceptHMRUpdate, defineStore } from 'pinia';
import { segmentVersionKey } from '@/constants/modules/segment-v2/segment-adaptor';
import { DataSource } from '@/views/modules/segment-v2/types/data-source';

import api from '@/services/api';
import segmentV2Model from '@/models/segment-v2/segment';

const { fetchSourcesDetailModel } = segmentV2Model();
export const useBuilderConfigStore = defineStore({
  id: 'builder_config',

  state: (): { ruleSetRoot: RuleSetRoot } => ({
    ruleSetRoot: {
      preview_ref: '',
      rule_set_list: [],
      group_equation: '',
      view_table_list: [],
      view_column_list: [],
      view_group_by_list: [],
      view_filter_list: [],
    },
  }),
  getters: {
    getRoot(state) {
      return this.ruleSetRoot;
    },
  },
  actions: {
    update(state: RuleSetRoot) {
      this.ruleSetRoot = state;
    },
    reset() {
      this.ruleSetRoot = {
        preview_ref: '',
        rule_set_list: [],
        group_equation: '',
        view_table_list: [],
        view_column_list: [],
        view_group_by_list: [],
        view_filter_list: [],
      };
    },
  },
  persist: {
    key: 'segment_config_builder',
    storage: localStorage,
  },
});

export const useBuilderTreeStore = defineStore({
  id: 'builder_tree',

  state: (): { tree: TreeNode } => ({
    tree: {
      id: '',
      type: 'term',
      depth: 0,
    },
  }),
  getters: {
    getTree(state) {
      return this.tree;
    },
  },
  actions: {
    update(state: TreeNode) {
      this.tree = state;
    },
  },
  persist: {
    key: 'segment_config_tree',
    storage: localStorage,
  },
});

// Advance View Config
export const useBuilderAdvanceViewStore = defineStore({
  id: 'advance_view_store',

  state: (): { viewAdvanceConfig: any | null } => ({
    viewAdvanceConfig: null,
  }),
  getters: {
    getViewAdvance(state) {
      return state.viewAdvanceConfig;
    },
  },
  actions: {
    update(config: any | null) {
      this.viewAdvanceConfig = config;
    },
    reset() {
      this.viewAdvanceConfig = null;
    },
  },
});

// ในไฟล์ store เดิมของคุณ
export const useSourceDetailStore = defineStore({
  id: 'segment_source_detail',

  state: (): { sourceDetail: DataSource | null } => ({
    sourceDetail: null,
  }),

  getters: {
    getSourceDetail(state): DataSource | null {
      return state.sourceDetail;
    },
    getStructureSourceDetail(state) {
      return state.sourceDetail?.structure;
    },
  },

  actions: {
    async fetchSourceDetail() {
      const response = await api.apiRequest(fetchSourcesDetailModel);
      this.sourceDetail = response.data as DataSource;
    },
    async refreshFavoriteColumns() {
      const response = await api.apiRequest(fetchSourcesDetailModel);
      const ds = response.data as DataSource;
      const favorites = ds?.favorite_columns ?? [];

      if (this.sourceDetail) {
        this.$patch((state) => {
          state.sourceDetail!.favorite_columns = favorites;
        });
      } else {
        this.sourceDetail = ds;
      }
      return favorites;
    },

    update(sourceDetail: DataSource) {
      this.sourceDetail = sourceDetail;
    },
    reset() {
      this.sourceDetail = null;
    },
  },

  persist: {
    key: 'segment_source_detail',
    storage: sessionStorage,
  },
});

//Martech
export const useSegmentVersion = defineStore({
  id: 'segment_version',

  state: (): { segmentVersion: string | null } => ({
    segmentVersion: null,
  }),
  getters: {
    getSegmentVersion(state): string | null {
      return state.segmentVersion;
    },
  },
  actions: {
    async fetchSegmentVersion() {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      const brandRef = brandService.getBrandRefParams();
      if (!brandRef) return;
      const segmentV2BrandDev = ['BTSIT17XC9ZM', 'BL6ZLW8PXBXA', 'BNRE0BK41S3B'];
      const segmentV2BrandProd = ['BC47UQQHYVIF'];
      const nonProdEnvs = ['development', 'qa', 'uat', 'demo'];
      const isNonProd = nonProdEnvs.includes(import.meta.env.VITE_BASE_ENV_URL);

      if (isNonProd) {
        if (segmentV2BrandDev.includes(brandRef)) {
          this.update('2.0.0');
        } else {
          this.update('1.0.0');
        }
      } else if (import.meta.env.VITE_BASE_ENV_URL === 'production') {
        if (segmentV2BrandProd.includes(brandRef)) {
          this.update('2.0.0');
        } else {
          this.update('1.0.0');
        }
      }
    },
    update(version: string) {
      this.segmentVersion = version;
    },
    reset() {
      this.segmentVersion = null;
    },
  },
  persist: {
    key: segmentVersionKey,
    storage: localStorage,
  },
});
//All data tab
export const useAllDataTab = defineStore({
  id: 'alldata_tab',
  state: (): { valueSegmentAllData: any } => ({
    valueSegmentAllData: {
      tableIDSelect: [],
      selectedTablesMain: [],
      selectedTables: [],
    },
  }),
  getters: {
    getValueAllDataTab(state): any {
      return state.valueSegmentAllData;
    },
  },
  actions: {
    update(new_tableIDSelect: any, new_selectedTablesMain: any, new_selectedTables: any) {
      this.valueSegmentAllData.tableIDSelect = new_tableIDSelect;
      this.valueSegmentAllData.selectedTablesMain = new_selectedTablesMain;
      this.valueSegmentAllData.selectedTables = new_selectedTables;
    },
    reset() {
      this.valueSegmentAllData.tableIDSelect = [];
      this.valueSegmentAllData.selectedTablesMain = [];
      this.valueSegmentAllData.selectedTables = [];
    },
  },
  persist: {
    key: 'segmentAllDataTab',
    storage: localStorage,
  },
});
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useBuilderConfigStore, import.meta.hot));
  import.meta.hot.accept(acceptHMRUpdate(useBuilderTreeStore, import.meta.hot));
  import.meta.hot.accept(acceptHMRUpdate(useSourceDetailStore, import.meta.hot));
  import.meta.hot.accept(acceptHMRUpdate(useSegmentVersion, import.meta.hot));
  import.meta.hot.accept(acceptHMRUpdate(useAllDataTab, import.meta.hot));
  import.meta.hot.accept(acceptHMRUpdate(useBuilderAdvanceViewStore, import.meta.hot));
}
